import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "EntesConformadores";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const ListarEnteConformador = () => {
  const [entes, setEntes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const navigate = useNavigate();

  /* =========================================
      AUTH
  ========================================= */
  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  /* =========================================
      PERMISOS
  ========================================= */
  const getModuloEntes = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloEntes = useMemo(() => getModuloEntes(), []);

  /* =========================================
      PERMISO LEER
  ========================================= */
  const puedeLeer =
    moduloEntes?.puedeLeer === true ||
    moduloEntes?.leer === true ||
    moduloEntes?.ver === true ||
    moduloEntes?.listar === true ||
    moduloEntes?.read === true ||
    moduloEntes?.view === true;

  const puedeCrear =
    moduloEntes?.puedeCrear === true ||
    moduloEntes?.crear === true ||
    moduloEntes?.create === true;

  const puedeActualizar =
    moduloEntes?.puedeActualizar === true ||
    moduloEntes?.actualizar === true ||
    moduloEntes?.editar === true ||
    moduloEntes?.update === true;

  const puedeEliminar =
    moduloEntes?.puedeEliminar === true ||
    moduloEntes?.eliminar === true ||
    moduloEntes?.delete === true;

  /* =========================================
      SESIÓN
  ========================================= */
  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/login", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  /* =========================================
      ELIMINAR
  ========================================= */
  const deleteEnte = useCallback(
    async (id) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar entes conformadores.",
          "warning"
        );
        return false;
      }

      try {
        const result = await Swal.fire({
          title: "¿Está seguro?",
          text: "¡No podrá revertir este proceso!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return false;

        setDeletingId(id);

        const { data } = await axios.delete(
          `/api/ente/${id}`,
          authConfig()
        );

        setEntes((prev) =>
          prev.filter((ente) => ente._id !== id)
        );

        Swal.fire({
          icon: "success",
          title: data?.message || "Ente eliminado",
          showConfirmButton: false,
          timer: 1500,
        });

        return true;
      } catch (error) {
        console.error(error);

        if (manejarErrorSesion(error)) return false;

        Swal.fire(
          "Error",
          error.response?.data?.message ||
            "No se pudo eliminar.",
          "error"
        );

        return false;
      } finally {
        setDeletingId(null);
      }
    },
    [puedeEliminar, manejarErrorSesion]
  );

  /* =========================================
      CARGAR DATOS
  ========================================= */
  const getData = useCallback(async () => {

    /* =========================================
        VALIDAR LECTURA
    ========================================= */
    if (!puedeLeer) {
      setEntes([]);
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.get(
        "/api/ente/listall/",
        authConfig()
      );

      const lista = Array.isArray(data?.entesConformadores)
        ? data.entesConformadores
        : [];

      /* =========================================
          FORMATEAR DATOS
      ========================================= */
      const listaFormateada = lista.map((ente) => ({
        ...ente,

        nombreEstado:
          ente?.estadoObjectId?.nombreEstado?.trim() ||
          "Estado no disponible",

        nombreCiudad:
          ente?.ciudadObjectId?.nombreCiudad?.trim() ||
          "Ciudad no disponible",
      }));

      /* =========================================
          ORDENAR DANE ASCENDENTE
      ========================================= */
      const listaOrdenada = [...listaFormateada].sort(
        (a, b) => {
          const daneA = parseInt(
            a?.coddaneEnteconformador || 0
          );

          const daneB = parseInt(
            b?.coddaneEnteconformador || 0
          );

          return daneA - daneB;
        }
      );

      setEntes(listaOrdenada);

    } catch (error) {
      console.error(error);

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message ||
          "No se pudieron cargar los entes.",
        "error"
      );

      setEntes([]);
    } finally {
      setLoading(false);
    }
  }, [puedeLeer, manejarErrorSesion]);

  useEffect(() => {
    getData();
  }, [getData]);

  /* =========================================
      TABLA
  ========================================= */
  const dataTabla = useMemo(
    () =>
      entes.map((ente) => ({
        ...ente,

        funciones: (
          <div className="d-flex gap-2 flex-wrap justify-content-end">

            <button
              className="btn btn-outline-dark btn-sm d-inline-flex align-items-center justify-content-center"
              style={{ width: 34, height: 34 }}
              onClick={() =>
                navigate(`/enteconformador/asociar/${ente._id}`)
              }
              title="Asociar Rector"
              type="button"
              disabled={loading || deletingId === ente._id}
            >
              <i className="fa-solid fa-link"></i>
            </button>

            <button
              className="btn btn-outline-dark btn-sm d-inline-flex align-items-center justify-content-center"
              style={{ width: 34, height: 34 }}
              onClick={() =>
                navigate(`/enteconformador/resoluciones/${ente._id}`)
              }
              title="Listar Resoluciones"
              type="button"
              disabled={loading || deletingId === ente._id}
            >
              <i className="fa-solid fa-list"></i>
            </button>

            {puedeActualizar && (
              <button
                className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() =>
                  navigate(`/enteconformador/ente/editar/${ente._id}`)
                }
                title="Editar Ente"
                type="button"
                disabled={loading || deletingId === ente._id}
              >
                <i className="fa-solid fa-pen-to-square"></i>
              </button>
            )}

            {puedeEliminar && (
              <button
                className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() => deleteEnte(ente._id)}
                title="Eliminar Ente"
                type="button"
                disabled={loading || deletingId === ente._id}
              >
                {deletingId === ente._id ? (
                  <i className="fa-solid fa-spinner fa-spin text-white"></i>
                ) : (
                  <i className="fa-solid fa-trash"></i>
                )}
              </button>
            )}
          </div>
        ),
      })),
    [
      entes,
      loading,
      deletingId,
      puedeActualizar,
      puedeEliminar,
      navigate,
      deleteEnte,
    ]
  );

  /* =========================================
      COLUMNAS
  ========================================= */
  const columns = useMemo(
    () => [
      {
        Header: "Dane",
        accessor: "coddaneEnteconformador",
      },

      {
        Header: "Nit",
        accessor: "nitEnteconformador",
      },

      {
        Header: "Nombre",
        accessor: "nombreEnteconformador",
      },

      {
        Header: "Teléfono 1",
        accessor: "telefono1Enteconformador",
      },

      {
        Header: "Estado",
        accessor: "nombreEstado",
      },

      {
        Header: "Ciudad",
        accessor: "nombreCiudad",
      },

      {
        Header: "Acciones",
        accessor: "funciones",
      },
    ],
    []
  );

  /* =========================================
      SIN PERMISO LECTURA
  ========================================= */
  if (!puedeLeer) {
    return (
      <div className="bg-body-tertiary py-4">
        <div className="container-fluid px-2 px-md-4">
          <div className="bg-white border rounded-4 shadow-sm p-4">

            <div className="text-center text-secondary py-5">

              <div className="display-6 mb-3">
                <i className="fa-solid fa-lock"></i>
              </div>

              <h5 className="fw-semibold text-dark">
                Sin permiso de lectura
              </h5>

              <p className="mb-4">
                No tienes permisos para visualizar los entes conformadores.
              </p>

              <button
                className="btn btn-outline-dark"
                type="button"
                onClick={() => navigate(-1)}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                Volver
              </button>

            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">

              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Entes Conformadores
                </h5>

                <small className="text-secondary">
                  Gestión institucional y resoluciones.
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">

                 <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() => navigate("/inicio")}
                  title="Volver al inicio"
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Inicio
                </button>


                <button
                  className="btn btn-outline-secondary"
                  onClick={getData}
                  disabled={loading}
                  type="button"
                >
                  <i
                    className={`fa-solid ${
                      loading
                        ? "fa-spinner fa-spin"
                        : "fa-rotate"
                    } me-2`}
                  ></i>

                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    onClick={() =>
                      navigate("/enteconformador/ente/nuevo/")
                    }
                    type="button"
                    disabled={loading}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div>

            {loading ? (
              <div className="text-center text-secondary py-5">

                <div className="spinner-border" />

                <div className="mt-3">
                  Cargando entes…
                </div>

              </div>
            ) : dataTabla.length > 0 ? (

              <div className="overflow-hidden">
                <DataTable
                  columns={columns}
                  data={dataTabla}
                />
              </div>

            ) : (
              <div className="text-center text-secondary py-5">

                <div className="display-6 mb-2">
                  🏛️
                </div>

                <div className="fw-semibold">
                  No hay entes registrados
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark mt-3"
                    type="button"
                    onClick={() =>
                      navigate("/enteconformador/ente/nuevo/")
                    }
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Registrar ente
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarEnteConformador;